const asErrorResponse = (body) => {
  if (body && typeof body === "object" && body.error && typeof body.error === "object") {
    return {
      ...body,
      ...body.error,
      errors: body.error.fields || body.error.errors || {},
    };
  }

  if (body && typeof body === "object") {
    return { ...body, errors: body.errors || {} };
  }

  return { message: body ? String(body) : "Request failed.", errors: {} };
};

export const getActionError = (action) => {
  const payload = action?.payload || {};
  const response = asErrorResponse(payload.message);
  return {
    status: payload.status ?? null,
    response,
    errors: response.errors || {},
  };
};

const nonEmptyText = (value) => (typeof value === "string" && value.trim() ? value.trim() : "");

// React/JS stringifies a non-string passed to ``new Error`` as "[object Object]".
// That placeholder is never a real message, so it must fall through to the
// caller's copy instead of surfacing an empty dialog.
const placeholders = new Set(["[object Object]", "Rejected", "Aborted", "undefined", "null"]);

const readableText = (value) => {
  const text = nonEmptyText(value);
  return placeholders.has(text) ? "" : text;
};

const fieldResolutionText = (fields) => {
  if (!fields || typeof fields !== "object" || Array.isArray(fields)) return [];
  return Object.entries(fields).flatMap(([field, messages]) => {
    const values = Array.isArray(messages) ? messages : [messages];
    const label = field.replace(/[_-]+/g, " ").replace(/^./, (character) => character.toUpperCase());
    return values
      .map(readableText)
      .filter(Boolean)
      .map((message) => `${label}: ${message}`);
  });
};

/**
 * Resolve a human-readable error message from any error shape the app sees:
 * a Flask ``{error: {code, message, fields}, request_id}`` body, a legacy
 * ``{message}`` body, a rejected thunk action, or a plain string.
 *
 * The calendar previously read ``error.response.data.message`` directly, which
 * is undefined for a Flask error envelope, so the dialog/toast rendered empty
 * (SOF-426 issues 06 and 39).
 */
export const readableApiError = (value, fallback = "Something went wrong. Please try again.") => {
  const candidate = value && typeof value === "object" && value.payload !== undefined ? value.payload : value;
  if (candidate?.status === 413 || candidate?.response?.status === 413) {
    return "The upload is too large. Choose a smaller file and try again.";
  }
  if (typeof candidate === "string") return readableText(candidate) || fallback;
  if (candidate == null) return fallback;

  const errorObject = candidate && typeof candidate === "object" && candidate.error && typeof candidate.error === "object"
    ? candidate.error
    : {};
  const reason = [
    candidate && candidate.userMessage,
    errorObject.message,
    candidate && candidate.message,
    candidate && candidate.error && typeof candidate.error === "string" ? candidate.error : "",
  ].map(readableText).find(Boolean);
  const fields = fieldResolutionText(
    (candidate && candidate.fields) || errorObject.fields || (candidate && candidate.errors),
  );

  const parts = [reason, ...fields].filter(Boolean);
  return parts.length > 0 ? [...new Set(parts)].join(" ") : fallback;
};

export default getActionError;
